import { CloseOutlined } from '@mui/icons-material';
import { Button } from 'finara-components';
import React, { useState, useEffect } from 'react';

import { Input } from '@/components/common/Input';
import type { UserDetails } from '@/types/user';

interface EditUserInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name?: string; rfc?: string; curp?: string; address?: string }) => void;
  userDetails: UserDetails | undefined;
  isLoading?: boolean;
}

export const EditUserInfoModal: React.FC<EditUserInfoModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userDetails,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    rfc: '',
    curp: '',
    address: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (userDetails) {
      setFormData({
        name: userDetails.name || '',
        rfc: userDetails.rfc || '',
        curp: userDetails.curp || '',
        address: userDetails.address || '',
      });
    }
  }, [userDetails]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors['name'] = 'El nombre es requerido';
    }

    if (formData.rfc) {
      const rfcRegex =
        /^([A-ZÑ\x26]{3,4}([0-9]{2})(0[1-9]|1[0-2])(0[1-9]|1[0-9]|2[0-9]|3[0-1]))([A-Z\d]{3})?$/;
      if (!rfcRegex.test(formData.rfc)) {
        newErrors['rfc'] = 'RFC inválido. Formato esperado: XAXX010101000';
      }
    }

    if (formData.curp) {
      const curpRegex =
        /^[A-Z]{4}[0-9]{6}[HM]{1}[A-Z]{2}[BCDFGHJKLMNPQRSTVWXYZ]{3}([A-Z]{2})?([0-9]{2})?$/;
      if (!curpRegex.test(formData.curp)) {
        newErrors['curp'] = 'CURP inválido. Debe tener el formato correcto';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData: { name?: string; rfc?: string; curp?: string; address?: string } = {};

    if (formData.name && formData.name !== userDetails?.name) {
      submitData.name = formData.name;
    }
    if (formData.rfc !== userDetails?.rfc) {
      submitData.rfc = formData.rfc || undefined;
    }
    if (formData.curp !== userDetails?.curp) {
      submitData.curp = formData.curp || undefined;
    }
    if (formData.address !== userDetails?.address) {
      submitData.address = formData.address || undefined;
    }

    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({ name: '', rfc: '', curp: '', address: '' });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black opacity-60 cursor-default"
        onClick={handleClose}
        type="button"
        aria-label="Cerrar modal"
      />

      {/* Modal */}
      <div className="relative z-10 bg-surface border border-black/10 dark:border-white/15 rounded-lg shadow-2xl shadow-black/60 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-brand-white">Editar Información Personal</h2>
          <button
            onClick={handleClose}
            className="p-2 text-muted hover:text-brand-white transition-colors"
            disabled={isLoading}
          >
            <CloseOutlined />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <Input
                id="name"
                label="Nombre Completo"
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Juan Pérez García"
                required
              />
              {errors['name'] && <p className="text-sm text-red-400 mt-1">{errors['name']}</p>}
            </div>

            <div>
              <Input
                id="rfc"
                label="RFC"
                type="text"
                value={formData.rfc}
                onChange={e =>
                  setFormData(prev => ({ ...prev, rfc: e.target.value.toUpperCase() }))
                }
                placeholder="Ej: XAXX010101000"
                maxLength={13}
              />
              {errors['rfc'] && <p className="text-sm text-red-400 mt-1">{errors['rfc']}</p>}
              <p className="text-xs text-muted mt-1">13 caracteres</p>
            </div>

            <div>
              <Input
                id="curp"
                label="CURP"
                type="text"
                value={formData.curp}
                onChange={e =>
                  setFormData(prev => ({ ...prev, curp: e.target.value.toUpperCase() }))
                }
                placeholder="Ej: XAXX010101HDFXXX00"
                maxLength={18}
              />
              {errors['curp'] && <p className="text-sm text-red-400 mt-1">{errors['curp']}</p>}
              <p className="text-xs text-muted mt-1">18 caracteres</p>
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-brand-white mb-1">
                Dirección
              </label>
              <textarea
                id="address"
                value={formData.address}
                onChange={e => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Ej: Calle Principal #123, Col. Centro, Ciudad, Estado, CP 12345"
                rows={3}
                className="w-full rounded-lg border border-border bg-surface text-brand-white placeholder:text-muted px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue transition-colors disabled:opacity-50"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
            <Button variant="white" onClick={handleClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="black" disabled={isLoading}>
              {isLoading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
